import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, viewChildren } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatOption, MatSelect } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';

import { AppStringsPipe } from '../shared/pipes/appstrings.pipe';
import { default as appString } from '../../assets/resources/appstrings.da.json';
import { RegAnimalWeightsComponent } from '../reg-animal-weights/reg-animal-weights.component';
import { BatchWeighingStoreCommand } from '../shared/storage/data-store/model/store-batch-weighing-save-info';
import { UserService } from '../shared/services/user.service';
import { RoutePaths } from '../app.routes';
import { UuidService } from '../shared/services/uuid.service';
import { TimeService } from '../shared/services/time.service';
import { BatchWeighingDomainService } from '../shared/domain/services/batch-weighings/batch-weighing.domain.service';
import { DataStoreReaderService } from '../shared/services/data-store/viewmodels/data-store-reader-viewmodels.service';
import { StringUuid } from '../shared/domain/model/uuid/string-uuid';
import { SaveLocalService } from '../shared/services/data-store/save-local.service';
import { PigGender } from '../shared/domain/model/pig-gender.type';
import { PigGenderType } from '../shared/domain/model/pig-gender-type';
import { Uuid } from '../shared/domain/model/uuid/uuid.type';
import { WeighingTypeEnum } from '../shared/domain/model/model-enums';
import { AnimalWeighingSaveInfoViewModel } from '../shared/viewmodels/shared/animal-weighing-save-info.viewmodel';
import { AnimalWeightsViewModel } from '../shared/viewmodels/shared/animal-weights.viewmodel';
import { UpdateInfoViewModel } from '../shared/viewmodels/shared/update.viewmodel';

@Component({
  selector: 'app-weighing',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatOption,
    MatSelect,
    MatCardModule,
    MatListModule,
    MatTableModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    MatGridListModule,
    AppStringsPipe,
    RegAnimalWeightsComponent,
  ],
  templateUrl: './batch-weighing.component.html',
  // styleUrl: './weighing.component.scss',
  providers: [BatchWeighingDomainService, UserService, SaveLocalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchWeighingComponent implements AfterViewInit {
  protected readonly domainService = inject(BatchWeighingDomainService);
  private readonly dataStoreReaderService = inject(DataStoreReaderService);
  private readonly timeService = inject(TimeService);
  private readonly userInfoService = inject(UserService);
  private readonly saveService = inject(SaveLocalService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly uuidService = inject(UuidService);
  protected readonly batchWeighingId = input.required<string>();
  protected readonly batchWeighingUuid = computed(() => new StringUuid(this.batchWeighingId()));
  protected readonly batchWeighingViewModel = computed(() => this.dataStoreReaderService.getBatchWeighingViewModel(this.batchWeighingUuid()));

  protected genderGroup: FormGroup<{ genderSelect: FormControl<number | null | undefined> }>;
  private originalGenderValue: number | null | undefined = null;
  protected genderSelect = this.fb.control<number | null | undefined>(undefined);

  protected regAnimalWeightComponents = viewChildren(RegAnimalWeightsComponent);

  constructor(private doc: ElementRef) {
    this.genderGroup = this.fb.nonNullable.group({ genderSelect: this.genderSelect });
  }

  private hasGenderChanged(): boolean {
    return this.genderSelect.dirty && this.getGenderSelectValue() !== this.originalGenderValue;
  }

  protected ngOnChanges() {
    const genderSelectValue = this.batchWeighingViewModel().pigGender.id;
    this.originalGenderValue = genderSelectValue;
    this.genderSelect.setValue(genderSelectValue);
  }

  ngAfterViewInit() {
    this.triggerOutlierCalculation();
  }

  private triggerOutlierCalculation(): void {
    this.regAnimalWeightComponents().forEach((regAnimalWeightComponent) => {
      regAnimalWeightComponent.setOutlier();
    });
  }

  private getGenderSelectValue(): number | null | undefined {
    return this.genderSelect.value;
  }

  protected hasPreviousBatchWeighing(): boolean {
    const previousBwId = this.batchWeighingViewModel().adjacentBws.previousBwId;
    return previousBwId !== undefined && previousBwId !== null;
  }

  protected hasNextBatchWeighing(): boolean {
    const nextBwId = this.batchWeighingViewModel().adjacentBws.nextBwId;
    return nextBwId !== undefined && nextBwId !== null;
  }

  private getSaveInfosFromComponents(components: RegAnimalWeightsComponent[]): AnimalWeighingSaveInfoViewModel[] {
    const saveInfos = components.map((component) => {
      return component.animalWeighingSaveInfoViewModel();
    });
    return saveInfos;
  }

  private getChangedSubComponents(): RegAnimalWeightsComponent[] {
    return this.regAnimalWeightComponents().filter((t) => t.isDirty());
  }

  private getAnimalWeightsFromComponents(): AnimalWeightsViewModel[] {
    const animalWeights = this.regAnimalWeightComponents().map((component) => {
      return component.getAnimalWeightsViewModel();
    });
    return animalWeights;
  }

  private getValidInputValues(): AnimalWeightsViewModel[] {
    const animalWeights = this.getAnimalWeightsFromComponents();
    return animalWeights.filter((t) => t.animals > 0 && t.weight > 0);
  }

  protected numberOfAnimalsTotal(): number {
    const numAnimals: number[] = this.getValidInputValues().map((t) => t.animals);
    return this.domainService.numberOfAnimals(numAnimals);
  }

   isOutlierWeightForPen(penAnimalWeight: AnimalWeightsViewModel): boolean {
    const weighings = this.getValidInputValues();

    return this.domainService.isOutlierWeightForPen(penAnimalWeight.animals, penAnimalWeight.weight, weighings);
  }

  onInputFormChanged(event: Event): void {
    console.log('Child form changed:', event);
    this.triggerOutlierCalculation();
  }

  protected getAverageWeight(): number {
    const numWeight: number[] = this.getValidInputValues().map((t) => t.weight);
    const numAnimals: number[] = this.getValidInputValues().map((t) => t.animals);
    return this.domainService.averageWeightRounded(numAnimals, numWeight);
  }

  protected averageWeightForPen(index: number): number {
    const numAnimals = this.getValidInputValues().map((t) => t.animals)[index];
    const weight = this.getValidInputValues().map((t) => t.weight)[index];

    if (!numAnimals) {
      throw new Error(`Number of animals is undefined for index: ${index}`);
    }

    if (!weight) {
      throw new Error(`Number of weight is undefined for index: ${index}`);
    }

    return this.domainService.averageWeightForPen(numAnimals, weight);
  }

  async saveGotoPreviousBatch() {
    this.save();
    const previousBwId = this.batchWeighingViewModel().adjacentBws.previousBwId;
    if (!previousBwId) {
      return;
    }
    await this.navigateToBatch(previousBwId);
  }

  async saveAndNavigateToNextBatch() {
    this.save();
    const nextBwId = this.batchWeighingViewModel().adjacentBws.nextBwId;
    if (!nextBwId) {
      return;
    }
    await this.navigateToBatch(nextBwId);
  }

  async navigateToBatch(batchId: string) {
    await this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([`/${RoutePaths.BATCH_WEIGHING}/${batchId}`], {
        queryParams: { batchWeighingId: batchId },
      });
    });
  }

  protected save() {
    const changedComp = this.getChangedSubComponents();
    const userRegistrations = this.getSaveInfosFromComponents(changedComp);
    const hasGenderChanged = this.hasGenderChanged();
    if (userRegistrations.length === 0 && !hasGenderChanged) {
      console.log('no changed values found. Save operation aborted.');
      return;
    }
    let batchWeighingStoreCommand: BatchWeighingStoreCommand | undefined = undefined;
    if (hasGenderChanged) {
      const pigGender = this.getPigGenderSelected();
      batchWeighingStoreCommand = this.createBatchWeighingStoreCommand(pigGender, this.batchWeighingViewModel().id);
    }
    this.saveService.SaveBatchWeighing(userRegistrations, batchWeighingStoreCommand);
    this.makeComponentsPristine(changedComp);
  }

  private getPigGenderSelected(): PigGender | undefined {
    const genderSelectValue = this.getGenderSelectValue();
    if (genderSelectValue === null || genderSelectValue === undefined) {
      return undefined;
    }
    const gender = PigGenderType.fromId(genderSelectValue);
    return gender;
  }

  private createBatchWeighingStoreCommand(
    pigGender: PigGenderType | undefined,
    batchWeighingId: Uuid
  ): BatchWeighingStoreCommand | undefined {
    if (pigGender === undefined || pigGender === null) {
      return undefined;
    }
    const newId = this.uuidService.getNewUuid();
    const update = this.batchWeighingViewModel().update;
    return new BatchWeighingStoreCommand(
      newId,
      batchWeighingId,
      pigGender,
      new UpdateInfoViewModel(
        update.lastServerUpdate,
        this.timeService.getNow(),
        this.userInfoService.getUserInfo())
    );
  }

  private makeComponentsPristine(components: RegAnimalWeightsComponent[]): void {
    components.forEach((component) => {
      component.makePristine();
    });
    this.markGenderAsPristine();
  }

  markGenderAsPristine() {
    this.genderSelect.markAsPristine();
    this.originalGenderValue = this.getGenderSelectValue();
  }

  protected getWeighingType(): string {
    switch (this.batchWeighingViewModel().weighingType.type) {
      case WeighingTypeEnum.Interim:
        return appString.GENERAL.INTERIMWEIGHING;
      case WeighingTypeEnum.Initial:
        return appString.GENERAL.INITIALWEIGHING;
      case WeighingTypeEnum.Final:
        return appString.GENERAL.ENDWEIGHING;
      default:
        throw new Error('Invalid WeighingType: ' + this.batchWeighingViewModel().weighingType.type);
    }
  }
}

export class PigGenderInput {
  id: number;

  constructor(id: number) {
    this.id = id;
  }
}
